import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import _ from 'lodash';
import styles from './AssetConcentrationSummary.module.sass';
import Spinner from '../Spinner';
import MetricInsightModal from 'components/Modal/MetricInsightModal';
import { RootState } from '../../redux/store';
import { Activity } from 'lucide-react';

const AssetConcentrationSummary = React.memo(({ cachedData }) => {
  // Always call hooks at the top level
  const reduxPairData = useSelector((state: RootState) => state.portfolio.pairData);
  const selectedContract = useSelector((state: RootState) => state.user.selectedContract);
  const loading = useSelector((state: RootState) => state.portfolio.loading.pairData);
  const membershipStatus = useSelector((state: RootState) => state.user.membershipStatus);
  const selectedToken = useSelector((state: RootState) => {
    const fullToken = state.user.selectedToken;
    return fullToken?.split('-')[0] || '';
  });

  // Use cached data (with priority) or Redux state
  const pairData = Array.isArray(cachedData) && cachedData.length > 0 ? cachedData : reduxPairData;

  // Animation state
  const [animate, setAnimate] = useState(false);
  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState({
    name: '',
    score: 0,
    description: ''
  });

  // Trigger animation when data changes
  useEffect(() => {
    if (pairData && pairData.length > 0) {
      setAnimate(false);
      const timer = setTimeout(() => {
        setAnimate(true);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [pairData, selectedContract]);


  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.content}>
          <Spinner />
        </div>
      </div>
    );
  }

  // Find the metrics object in the array
  const metricsObject = pairData?.find(item => 
    'buy_sell_pressure_score' in item && 
    'liquidity_health_score' in item && 
    'market_activity_score' in item && 
    'price_stability_score' in item
  );

  if (!metricsObject && !loading) {
    return (
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.emptyState}>
            <div className={styles.emptyStateIcon}>
              <Activity size={48} opacity={0.5} />
            </div>
            <h3 className={styles.emptyStateTitle}>No Metrics Data</h3>
            <p className={styles.emptyStateDescription}>
              Concentration metrics unavailable for this asset.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const calculateAggregateMetrics = () => {
    if (!pairData || !metricsObject) return null;

    return {
      buySellPressure: metricsObject.buy_sell_pressure_score,
      liquidityHealth: metricsObject.liquidity_health_score,
      priceStability: metricsObject.price_stability_score,
      marketActivity: metricsObject.market_activity_score,
      overallScore: (
        metricsObject.buy_sell_pressure_score * 0.25 +
        metricsObject.liquidity_health_score * 0.35 +
        metricsObject.price_stability_score * 0.25 +
        metricsObject.market_activity_score * 0.15
      )
    };
  };

  const aggregateMetrics = calculateAggregateMetrics();

  if (!aggregateMetrics) {
    return (
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.emptyState}>
            <div className={styles.emptyStateIcon}>
              <Activity size={48} opacity={0.5} />
            </div>
            <h3 className={styles.emptyStateTitle}>No Pair Data</h3>
            <p className={styles.emptyStateDescription}>
              Unable to calculate concentration metrics.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // const getHealthStatus = (score) => {
  //   if (score < 2) return 'COMPROMISED';
  //   if (score < 3.5) return 'UNSTABLE';
  //   return 'HEALTHY';
  // };

  const getColorStyles = (value) => {
    if (value < 2) {
      return {
        progress: {
          background: '#f44336',
          boxShadow: '0 0 10px rgba(244, 67, 54, 0.4)'
        },
        card: {
          borderColor: 'rgba(244, 67, 54, 0.3)',
          boxShadow: '0 0 15px rgba(244, 67, 54, 0.2)'
        },
        glow: {
          textShadow: '0 0 10px rgba(244, 67, 54, 0.8)'
        }
      };
    }
    if (value < 3.5) {
      return {
        progress: {
          background: '#9c27b0',
          boxShadow: '0 0 10px rgba(156, 39, 176, 0.4)'
        },
        card: {
          borderColor: 'rgba(156, 39, 176, 0.3)',
          boxShadow: '0 0 15px rgba(156, 39, 176, 0.2)'
        },
        glow: {
          textShadow: '0 0 10px rgba(156, 39, 176, 0.8)'
        }
      };
    }
    return {
      progress: {
        background: '#73F6FF',
        boxShadow: '0 0 10px rgba(115, 246, 255, 0.3)'
      },
      card: {
        borderColor: 'rgba(115, 246, 255, 0.2)',
        boxShadow: '0 0 15px rgba(115, 246, 255, 0.1)'
      },
      glow: {
        textShadow: '0 0 10px rgba(115, 246, 255, 0.8)'
      }
    };
  };
  
  const getMetricDescription = (metricName) => {
    const descriptions = {
      buySellPressure: 'Measures the ratio of buy to sell orders over the last 24 hours. Higher scores indicate stronger buying pressure relative to selling pressure, suggesting positive market sentiment.',
      liquidityHealth: 'Evaluates the depth and resilience of the asset\'s liquidity pools. A high score means the asset has sufficient liquidity across multiple DEXs, reducing slippage and price impact.',
      priceStability: 'Analyzes price volatility and resistance to manipulation. Higher scores indicate more stable price action with fewer extreme price swings and a more orderly market.',
      marketActivity: 'Measures trading volume, transaction count, and user participation. A high score indicates an active, engaged market with strong trading interest.',
      overallScore: 'A weighted composite score of all metrics, with Liquidity Health (35%), Buy/Sell Pressure (25%), Price Stability (25%), and Market Activity (15%). This score reflects the overall health and quality of the asset.'
    };
    
    return descriptions[metricName] || '';
  };

  const openMetricModal = (metricKey, metricName, score) => {
    setSelectedMetric({
      name: metricName,
      score: score,
      description: getMetricDescription(metricKey)
    });
    setModalOpen(true);
  };
  
  const handleUpgradeClick = () => {
    window.location.href = '/billing';
  };

  const MetricScore = ({ label, value, metricKey }) => {
    const colorStyles = getColorStyles(value);
    
    return (
      <div 
        className={styles.metric}
        style={{
          ...colorStyles.card,
          cursor: 'pointer',
          position: 'relative'
        }}
        onClick={() => openMetricModal(metricKey, label, value)}
      >
        <div className={styles.metricContent}>
          <div className={styles.metricStats}>
            <div className={styles.metricLabel}>{label}</div>
            <div className={styles.scoreContent}>
              <div className={styles.scoreSegments}>
                {[0,1,2,3,4].map((segment) => {
                  const isFullyFilled = segment < Math.floor(value);
                  const isPartiallyFilled = segment === Math.floor(value);
                  const partialFillPercentage = isPartiallyFilled ? (value % 1) * 100 : 0;
                  
                  // Animation delays for segments
                  const animationDelay = animate ? `${segment * 120}ms` : '0ms';
                  
                  let segmentStyle = {
                    transition: `all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) ${animationDelay}`
                  };
                  
                  if (isFullyFilled && animate) {
                    segmentStyle = {
                      ...segmentStyle,
                      ...colorStyles.progress
                    };
                  } else if (isPartiallyFilled && partialFillPercentage > 0 && animate) {
                    const color = colorStyles.progress.background;
                    segmentStyle = {
                      ...segmentStyle,
                      background: `linear-gradient(to right, ${color} ${partialFillPercentage}%, rgba(115, 246, 255, 0.1) ${partialFillPercentage}%)`,
                      boxShadow: colorStyles.progress.boxShadow
                    };
                  }
                  
                  return (
                    <div 
                      key={segment}
                      className={`${styles.segment} ${isFullyFilled && animate ? styles.segmentFilled : ''}`}
                      style={segmentStyle}
                    />
                  );
                })}
              </div>
              <div 
                className={styles.metricScoreDisplay}
                style={value < 3.5 ? colorStyles.glow : undefined}
              >
                <span className={styles.metricValue}>{value.toFixed(2)}</span>
                <span className={styles.metricMaxValue}>/5.00</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.metricsSection}>
          <div className={styles.metrics}>
            <div className={styles.overallScore}>
              <div className={styles.scoreLabel}>{`${selectedToken} Health Score`}</div>
              {loading ? (
                <div className={styles.scoreContent} style={{ minHeight: '160px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Spinner />
                </div>
              ) : (
                <div 
                  className={styles.scoreContent} 
                  style={{ cursor: 'pointer' }}
                  onClick={() => openMetricModal('overallScore', 'Overall Health Score', aggregateMetrics.overallScore)}
                >
                  <div className={styles.scoreSegments}>
                    {[0,1,2,3,4].map((segment) => {
                      const isFullyFilled = segment < Math.floor(aggregateMetrics.overallScore);
                      const isPartiallyFilled = segment === Math.floor(aggregateMetrics.overallScore);
                      const partialFillPercentage = isPartiallyFilled ? (aggregateMetrics.overallScore % 1) * 100 : 0;
                      
                      // Animation delays for segments
                      const animationDelay = animate ? `${segment * 150}ms` : '0ms';
                      
                      let segmentStyle = {
                        transition: `all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) ${animationDelay}`
                      };
                      
                      if (isFullyFilled && animate) {
                        segmentStyle = {
                          ...segmentStyle,
                          ...getColorStyles(aggregateMetrics.overallScore).progress
                        };
                      } else if (isPartiallyFilled && partialFillPercentage > 0 && animate) {
                        const color = getColorStyles(aggregateMetrics.overallScore).progress.background;
                        segmentStyle = {
                          ...segmentStyle,
                          background: `linear-gradient(to right, ${color} ${partialFillPercentage}%, rgba(115, 246, 255, 0.1) ${partialFillPercentage}%)`,
                          boxShadow: getColorStyles(aggregateMetrics.overallScore).progress.boxShadow
                        };
                      }
                      
                      return (
                        <div 
                          key={segment}
                          className={`${styles.segment} ${isFullyFilled && animate ? styles.segmentFilled : ''}`}
                          style={segmentStyle}
                        />
                      );
                    })}
                  </div>
                  <div className={styles.scoreInfo}>
                    <div className={styles.scoreValue}
                        style={aggregateMetrics.overallScore < 3.5 ? getColorStyles(aggregateMetrics.overallScore).glow : undefined}
                    >
                      {aggregateMetrics.overallScore.toFixed(2)}
                      <span className={styles.scoreMax}>/5.00</span>
                    </div>
                  </div>
                  <button 
                    className={styles.actionButton}
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent triggering the modal
                      window.location.href = '/discovery';
                    }}
                    style={aggregateMetrics.overallScore < 3.5 ? {
                      background: 'rgba(244, 67, 54, 0.1)',
                      borderColor: 'rgba(244, 67, 54, 0.3)',
                      color: '#f44336'
                    } : {
                      background: 'rgba(115, 246, 255, 0.1)',
                      borderColor: 'rgba(115, 246, 255, 0.3)',
                      color: '#73F6FF'
                    }}
                  >
                    {aggregateMetrics.overallScore < 2 ? (
                      <>Discover <span className={styles.actionArrow}>→</span></>
                    ) : aggregateMetrics.overallScore < 3.5 ? (
                      <>Other Assets <span className={styles.actionArrow}>→</span></>
                    ) : (
                      <>View Similar<span className={styles.actionArrow}>→</span></>
                    )}
                  </button>
                </div>
              )}
            </div>
            
            <div className={styles.metricRow}>
              <MetricScore 
                label="Buy/Sell Pressure"
                value={aggregateMetrics.buySellPressure}
                metricKey="buySellPressure"
              />
              <MetricScore 
                label="Liquidity Health"
                value={aggregateMetrics.liquidityHealth}
                metricKey="liquidityHealth"
              />
            </div>

            <div className={styles.metricRow}>
              <MetricScore 
                label="Price Stability"
                value={aggregateMetrics.priceStability} 
                metricKey="priceStability"
              />
              <MetricScore 
                label="Market Activity"
                value={aggregateMetrics.marketActivity}
                metricKey="marketActivity"
              />
            </div>
          </div>
        </div>
        
        {/* Modal for metric details */}
        <MetricInsightModal 
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          metricName={selectedMetric.name}
          score={selectedMetric.score}
          description={selectedMetric.description}
          membershipStatus={membershipStatus}
          onUpgrade={handleUpgradeClick}
        />
      </div>
    </div>
  );
});

export default AssetConcentrationSummary;